import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { Search, SlidersHorizontal, Grid, List } from '../icons';
import VendorCard from '../components/VendorCard';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { getVendors, getVendorCities, getCategories, liveSearchGoogleVendors, verifyVendor, unverifyVendor } from '../services/api';

function VendorListing() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { category } = useParams();
  const [searchParams] = useSearchParams();
  const [vendors, setVendors] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [googleVendors, setGoogleVendors] = useState([]);
  const [verifyingId, setVerifyingId] = useState(null);
  const [filters, setFilters] = useState({
    city: searchParams.get('city') || '',
    price_range: '',
    search: searchParams.get('search') || '',
    sort_by: '',
  });
  const [viewMode, setViewMode] = useState('grid');
  const [trustedOnly, setTrustedOnly] = useState(false);
  const [cities, setCities] = useState([]);
  const [categoryList, setCategoryList] = useState([]);
  const [loadError, setLoadError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const categoryNames = {
    venues: 'Venues', photographers: 'Photographers', caterers: 'Caterers',
    decorators: 'Decorators', 'mehendi-artists': 'Mehendi Artists',
    'djs-music': 'DJs & Music', 'beauty-makeup': 'Beauty & Makeup',
    florists: 'Florists', 'pandits-priests': 'Pandits & Priests',
    'wedding-planners': 'Wedding Planners', 'invitation-cards': 'Invitation Cards',
    jewellers: 'Jewellers', 'bridal-wear': 'Bridal Wear',
  };

  const PAGE_SIZE = 40;

  useEffect(() => {
    getVendorCities().then(res => setCities(res.data)).catch(() => {});
    // Tabs follow the real category list, so a category added in the database
    // (e.g. Panche Baja, which the homepage links to) gets a tab here too.
    getCategories().then(res => setCategoryList(Array.isArray(res.data) ? res.data : [])).catch(() => {});
  }, []);

  useEffect(() => {
    // Typing in the search box re-runs this on every keystroke; ignore answers to
    // superseded requests so a slow earlier response can't replace newer results.
    let cancelled = false;
    setLoading(true);
    setLoadError(false);
    setSyncing(false);
    setVendors([]);
    setGoogleVendors([]);
    getVendors({ category, ...filters, limit: PAGE_SIZE, offset: 0 })
      .then(async (res) => {
        if (cancelled) return;
        setVendors(res.data.vendors);
        setTotal(res.data.total);

        // Live Google Places search (works with or without a category)
        if (!filters.search && !filters.price_range) {
          setSyncing(true);
          try {
            const gRes = await liveSearchGoogleVendors(category || '', filters.city || '');
            if (!cancelled) setGoogleVendors(gRes.data.vendors || []);
          } catch {
            // Google results are extra; our own listings are already shown
          } finally {
            if (!cancelled) setSyncing(false);
          }
        }
      })
      // A failed request used to fall through to "No vendors found".
      .catch(() => { if (!cancelled) { setTotal(0); setLoadError(true); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [category, filters, reloadKey]);

  // Combine local DB vendors + live Google vendors (filter Google results by city if selected)
  const filteredGoogleVendors = filters.city
    ? googleVendors.filter(v => v.city && v.city.toLowerCase() === filters.city.toLowerCase())
    : googleVendors;
  const combinedVendors = [...vendors, ...filteredGoogleVendors];
  const hasVerifiedVendors = combinedVendors.some(v => v.is_verified);
  const allVendors = trustedOnly ? combinedVendors.filter(v => v.is_verified) : combinedVendors;

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const res = await getVendors({ category, ...filters, limit: PAGE_SIZE, offset: vendors.length });
      setVendors((prev) => [...prev, ...res.data.vendors]);
    } catch (err) {
      alert(t('vendorListing.loadMoreFailed'));
    } finally {
      setLoadingMore(false);
    }
  };

  const handleVerify = async (vendorId) => {
    setVerifyingId(vendorId);
    try {
      await verifyVendor(vendorId);
      setVendors((prev) => prev.map(v => v.id === vendorId ? { ...v, is_verified: true } : v));
    } catch (err) {
      alert('Failed to verify vendor');
    } finally {
      setVerifyingId(null);
    }
  };

  const handleUnverify = async (vendorId) => {
    setVerifyingId(vendorId);
    try {
      await unverifyVendor(vendorId);
      setVendors((prev) => prev.map(v => v.id === vendorId ? { ...v, is_verified: false } : v));
    } catch (err) {
      alert('Failed to unverify vendor');
    } finally {
      setVerifyingId(null);
    }
  };

  const isAdmin = user?.role === 'admin';

  // Keep the curated tab order; categories it doesn't know go at the end.
  const tabOrder = Object.keys(categoryNames);
  const tabRank = (slug) => (tabOrder.includes(slug) ? tabOrder.indexOf(slug) : tabOrder.length);
  const allCategories = [
    { slug: '', label: t('vendorListing.allTab') },
    ...(categoryList.length
      ? [...categoryList]
          .sort((a, b) => tabRank(a.slug) - tabRank(b.slug))
          .map((c) => ({ slug: c.slug, label: categoryNames[c.slug] || c.name }))
      : Object.entries(categoryNames).map(([slug, label]) => ({ slug, label }))),
  ];
  const categoryLabel = categoryNames[category] || categoryList.find((c) => c.slug === category)?.name;

  const hasFilters = !!(filters.city || filters.price_range || filters.search || trustedOnly);
  const clearFilters = () => {
    setTrustedOnly(false);
    setFilters((f) => ({ ...f, city: '', price_range: '', search: '' }));
  };

  return (
    <div className="vendor-listing-page">
      <div className="listing-hero">
        <h1>{categoryLabel || t('vendorListing.allVendors')} {t('vendorListing.inNepal')}</h1>
        <p>{t('vendorListing.findBest')} {(categoryLabel || 'wedding vendors').toLowerCase()} {t('vendorListing.forSpecialDay')}</p>
      </div>

      {/* Category Tab Bar */}
      <div className="category-tab-bar">
        <div className="category-tab-scroll">
          {allCategories.map((cat) => (
            <button
              key={cat.slug}
              className={`category-tab ${(category || '') === cat.slug ? 'active' : ''}`}
              onClick={() => navigate(cat.slug ? `/vendors/${cat.slug}` : '/vendors')}
            >
              {cat.label}
            </button>
          ))}
        </div>
      </div>

      {/* Trusted-Only Toggle */}
      <div className="trusted-toggle-bar">
        <label className="trusted-filter">
          <input
            type="checkbox"
            checked={trustedOnly}
            onChange={(e) => setTrustedOnly(e.target.checked)}
          />
          {t('vendorListing.trustedOnly')}
        </label>
      </div>

      <div className="container listing-container">
        {/* Filters sidebar */}
        <aside className="filters-sidebar">
          <h3><SlidersHorizontal size={18} /> {t('vendorListing.filters')}</h3>
          
          <div className="filter-group">
            <label>{t('common.search')}</label>
            <div className="filter-search">
              <Search size={16} />
              <input
                type="text"
                placeholder={t('vendorListing.searchPlaceholder')}
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              />
            </div>
          </div>

          <div className="filter-group">
            <label>{t('vendorListing.city')}</label>
            <select value={filters.city} onChange={(e) => setFilters({ ...filters, city: e.target.value })}>
              <option value="">{t('vendorListing.allCities')}</option>
              {cities.map(city => (
                <option key={city} value={city}>{city}</option>
              ))}
            </select>
          </div>

          {/* Stay visible while a price is selected: a price with no matches
              left no verified vendors, hid this select and stranded the filter. */}
          {(hasVerifiedVendors || filters.price_range) && (
            <div className="filter-group">
              <label>{t('vendorListing.priceRange')}</label>
              <select value={filters.price_range} onChange={(e) => setFilters({ ...filters, price_range: e.target.value })}>
                <option value="">{t('vendorListing.allPrices')}</option>
                <option value="budget">{t('vendorListing.budgetFriendly')}</option>
                <option value="moderate">{t('vendorListing.moderate')}</option>
                <option value="premium">{t('vendorListing.premium')}</option>
                <option value="luxury">{t('vendorListing.luxury')}</option>
              </select>
            </div>
          )}

          <div className="filter-group">
            <label>{t('vendorListing.sortBy')}</label>
            <select value={filters.sort_by} onChange={(e) => setFilters({ ...filters, sort_by: e.target.value })}>
              <option value="">{t('vendorListing.sortDefault')}</option>
              <option value="reviews">{t('vendorListing.sortReviews')}</option>
              <option value="rating">{t('vendorListing.sortRating')}</option>
              <option value="name">{t('vendorListing.sortAlpha')}</option>
            </select>
          </div>
        </aside>

        {/* Vendors grid */}
        <div className="listing-main">
          <div className="listing-topbar">
            <span className="listing-count">
              {trustedOnly ? allVendors.length : (total + filteredGoogleVendors.length)} {t('vendorListing.vendorsFound')}
              {trustedOnly && <span style={{ fontSize: '12px', color: '#16a34a', marginLeft: '6px' }}>{t('vendorListing.trusted')}</span>}
              {isAdmin && filteredGoogleVendors.length > 0 && !trustedOnly && <span style={{ fontSize: '12px', color: '#6b7280', marginLeft: '8px' }}>({total} local + {filteredGoogleVendors.length} from Google)</span>}
            </span>
            <div className="view-toggle">
              <button className={viewMode === 'grid' ? 'active' : ''} onClick={() => setViewMode('grid')}>
                <Grid size={18} />
              </button>
              <button className={viewMode === 'list' ? 'active' : ''} onClick={() => setViewMode('list')}>
                <List size={18} />
              </button>
            </div>
          </div>

          {loading ? (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>{t('vendorListing.finding')}</p>
            </div>
          ) : loadError ? (
            <div className="empty-state">
              <p>{t('vendorListing.loadError')}</p>
              <button className="btn btn-outline" onClick={() => setReloadKey((k) => k + 1)}>{t('common.retry')}</button>
            </div>
          ) : allVendors.length === 0 && !syncing ? (
            <div className="empty-state">
              <h3>{t('vendorListing.noVendorsFound')}</h3>
              <p>{t('vendorListing.noVendorsDesc')}</p>
              {hasFilters && (
                <button className="btn btn-outline" onClick={clearFilters}>{t('vendorListing.clearFilters')}</button>
              )}
            </div>
          ) : (
            <>
              {syncing && (
                <div style={{ textAlign: 'center', padding: '12px', marginBottom: '16px', background: '#fefce8', borderRadius: '8px', color: '#854d0e', fontSize: '14px' }}>
                  <div className="spinner" style={{ width: '18px', height: '18px', borderWidth: '2px', display: 'inline-block', verticalAlign: 'middle', marginRight: '8px' }}></div>
                  {t('vendorListing.searchingGoogle')}
                </div>
              )}
              <div className={`vendors-grid ${viewMode}`}>
                {allVendors.map((vendor) => (
                  <VendorCard
                    key={vendor.id}
                    vendor={vendor}
                    isAdmin={isAdmin}
                    onVerify={isAdmin && vendor.source !== 'google_live' ? handleVerify : null}
                    onUnverify={isAdmin && vendor.source !== 'google_live' ? handleUnverify : null}
                    verifyingId={verifyingId}
                  />
                ))}
              </div>
              {/* Also offered with "trusted only" on: that filter runs on the loaded
                  page, so verified vendors past it were otherwise unreachable. */}
              {vendors.length < total && (
                <div style={{ textAlign: 'center', marginTop: '2rem' }}>
                  <button
                    className="btn btn-outline"
                    onClick={loadMore}
                    disabled={loadingMore}
                    style={{ padding: '12px 40px', fontSize: '1rem' }}
                  >
                    {loadingMore ? t('common.loading') : `${t('vendorListing.loadMore')} (${vendors.length} of ${total})`}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default VendorListing;
